import { useState, useMemo } from 'react'
import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { isNullish } from '@cougargrades/utils/nullish'
import { computeUDI, deriveUDIInputs, getUDILabel, getUDIColor } from '@cougargrades/utils'
import type { CoursePlus, PopulatedGroupResult, LiteGroupResult } from '@cougargrades/models/dto'
import { GetGroupLayoutFromCategories } from '@cougargrades/models/dto'
import type { Comparator } from '@cougargrades/utils/comparator'
import { allGroupsDataQueryOptions, useAllGroups } from '../../lib/services/useAllGroups'
import { oneGroupDataQueryOptions, useOneGroup } from '../../lib/services/useOneGroup'
import type { SidebarItem } from '../../components/sidebarcontainer'
import { CourseCard, CourseCardSkeleton } from '../../components/ui/CourseCard'
import { GpaBadge } from '../../components/ui/GpaBadge'
import { Skeleton } from '../../components/ui/Skeleton'
import { cleanGroupName } from '../../lib/util'

// ─── Route ─────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/g/$groupId')({
  async loader(ctx) {
    const { groupId } = ctx.params
    ctx.context.queryClient.ensureQueryData(allGroupsDataQueryOptions())
    const oneGroup = await ctx.context.queryClient.ensureQueryData(
      oneGroupDataQueryOptions(ctx.params.groupId),
    )
    if (isNullish(oneGroup)) throw notFound()
    return oneGroup
  },
  head: (ctx) => {
    const group = ctx.loaderData
    return {
      meta: [
        { title: `${group?.name ? cleanGroupName(group.name) : 'Groups'} | Unilytics` },
        { name: 'description', content: `${group?.description ?? ''}` },
      ],
    }
  },
  component: RouteComponent,
  notFoundComponent: NotFoundComponent,
})

// ─── Helpers ───────────────────────────────────────────────────────────────

function gradeCountsFromEnrollment(enrollment: CoursePlus['enrollment']) {
  return {
    A: enrollment.totalA,
    B: enrollment.totalB,
    C: enrollment.totalC,
    D: enrollment.totalD,
    F: enrollment.totalF,
    W: enrollment.totalW,
  }
}

function wRateColor(pct: number): string {
  if (pct > 15) return '#DC2626'
  if (pct >= 8) return '#D97706'
  return '#059669'
}

function gpaColor(gpa: number): string {
  if (gpa >= 3.5) return '#059669'
  if (gpa >= 2.5) return '#10B981'
  if (gpa >= 1.5) return '#D97706'
  return '#DC2626'
}

type SortKey = 'name' | 'gpa' | 'udi' | 'wRate' | 'sections' | 'instructors'

interface CourseTableRow {
  id: string
  name: string
  avgGPA: number
  udi: number
  udiLabel: ReturnType<typeof getUDILabel>
  wRate: number
  sections: number
  instructors: number
  href: string
}

const PREFERRED_CATEGORY_ORDER: Comparator<string> = (a, b) => {
  const order = ['UH Core Curriculum (All)', 'Colleges/Schools', 'Other Groups']
  return order.indexOf(a) - order.indexOf(b)
}

// ─── Main component ─────────────────────────────────────────────────────────

function RouteComponent() {
  const { groupId } = Route.useParams()
  const { data, isPending } = useOneGroup(groupId)
  const { data: sidebarItems } = useAllGroups()

  const [sortKey, setSortKey] = useState<SortKey>('udi')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [showAllCourses, setShowAllCourses] = useState(false)

  const DID_LOAD = !isPending && !isNullish(data)

  const layout = DID_LOAD ? GetGroupLayoutFromCategories(data.categories) : null

  // ── Course table rows ──────────────────────────────────────────────────────
  const courseRows = useMemo<CourseTableRow[]>(() => {
    if (!DID_LOAD) return []
    return data.courses.map(course => {
      const grades = gradeCountsFromEnrollment(course.enrollment)
      const total = grades.A + grades.B + grades.C + grades.D + grades.F + grades.W
      const wRate = total > 0 ? (grades.W / total) * 100 : 0
      const udi = computeUDI(deriveUDIInputs({ grades, sections: course.sectionCount, schoolId: 'uh' }))
      return {
        id: course._id,
        name: course.description,
        avgGPA: course.GPA.average,
        udi: udi.udi,
        udiLabel: udi.label,
        wRate,
        sections: course.sectionCount,
        instructors: course.instructorCount,
        href: `/c/${encodeURIComponent(course._id)}`,
      }
    })
  }, [DID_LOAD, data])

  const sortedRows = useMemo(() => {
    const rows = [...courseRows]
    rows.sort((a, b) => {
      let diff = 0
      switch (sortKey) {
        case 'name': diff = a.id.localeCompare(b.id); break
        case 'gpa': diff = a.avgGPA - b.avgGPA; break
        case 'udi': diff = a.udi - b.udi; break
        case 'wRate': diff = a.wRate - b.wRate; break
        case 'sections': diff = a.sections - b.sections; break
        case 'instructors': diff = a.instructors - b.instructors; break
      }
      return sortDir === 'asc' ? diff : -diff
    })
    return rows
  }, [courseRows, sortKey, sortDir])

  const maxUDIId = useMemo(
    () => (courseRows.length > 0 ? courseRows.reduce((a, b) => (a.udi > b.udi ? a : b)).id : null),
    [courseRows],
  )
  const minUDIId = useMemo(
    () =>
      courseRows.length > 0
        ? courseRows
            .filter(r => r.sections >= 10)
            .reduce<CourseTableRow | null>((a, b) => (!a || b.udi < a.udi ? b : a), null)?.id ?? null
        : null,
    [courseRows],
  )

  // ── Hero stats ─────────────────────────────────────────────────────────────
  const heroStats = useMemo(() => {
    if (!DID_LOAD) return null
    const validGPA = courseRows.filter(r => r.avgGPA > 0)
    const avgGPA = validGPA.length > 0 ? validGPA.reduce((s, r) => s + r.avgGPA, 0) / validGPA.length : null
    const avgUDI = courseRows.length > 0 ? courseRows.reduce((s, r) => s + r.udi, 0) / courseRows.length : null
    return { avgGPA, avgUDI }
  }, [DID_LOAD, courseRows])

  // ── Top courses by enrollment ───────────────────────────────────────────────
  const topCourses = useMemo(() => {
    if (!DID_LOAD) return []
    return [...data.courses]
      .sort((a, b) => (b.enrollment.totalEnrolled ?? 0) - (a.enrollment.totalEnrolled ?? 0))
      .slice(0, 6)
  }, [DID_LOAD, data])

  // ── Department insights ────────────────────────────────────────────────────
  const insights = useMemo(() => {
    if (courseRows.length === 0) return null
    const hardest = courseRows.reduce((a, b) => (a.udi > b.udi ? a : b))
    const qualified = courseRows.filter(r => r.sections >= 10)
    const easiest = qualified.length > 0
      ? qualified.reduce((a, b) => (a.udi < b.udi ? a : b))
      : null
    const mostPopular = DID_LOAD
      ? data.courses.reduce((a, b) =>
          (b.enrollment.totalEnrolled ?? 0) > (a.enrollment.totalEnrolled ?? 0) ? b : a,
        )
      : null
    return { hardest, easiest, mostPopular }
  }, [courseRows, DID_LOAD, data])

  // ── Sort handler ───────────────────────────────────────────────────────────
  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('desc') }
  }

  const sortIcon = (key: SortKey) =>
    sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕'

  // ── Sidebar categories ─────────────────────────────────────────────────────
  const sidebarByCategory = useMemo(() => {
    if (!sidebarItems) return []
    const map = new Map<string, SidebarItem[]>()
    for (const item of sidebarItems) {
      const cat = item.categoryName || 'Other Groups'
      if (!map.has(cat)) map.set(cat, [])
      map.get(cat)!.push(item)
    }
    return [...map.entries()].sort(([a], [b]) => PREFERRED_CATEGORY_ORDER(a, b))
  }, [sidebarItems])

  const displayName = DID_LOAD ? cleanGroupName(data.name) : ''

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        style={{
          backgroundColor: 'var(--school-primary)',
          backgroundImage: `linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          padding: '40px 0 32px',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <div className="new-container">
          {isPending ? (
            <div>
              <Skeleton height={14} width={120} className="mb-3" style={{ background: 'rgba(255,255,255,0.12)' }} />
              <Skeleton height={36} width="55%" className="mb-3" style={{ background: 'rgba(255,255,255,0.1)' }} />
              <div className="flex gap-3">
                {[80, 100, 90, 85].map((w, i) => (
                  <Skeleton key={i} height={28} width={w} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 8 }} />
                ))}
              </div>
            </div>
          ) : DID_LOAD ? (
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* School badge */}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.6)',
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  University of Houston · Department
                </span>

                <h1
                  style={{
                    fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
                    fontWeight: 800,
                    color: '#fff',
                    margin: 0,
                    marginBottom: 16,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.15,
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {displayName}
                </h1>

                {/* Stat chips */}
                <div className="flex flex-wrap gap-2">
                  <StatChip label={`${data.courseCount} Courses`} />
                  <StatChip label={`${data.sectionCount.toLocaleString()} Sections`} />
                  {heroStats?.avgGPA != null && (
                    <StatChip
                      label={`Avg GPA: ${heroStats.avgGPA.toFixed(2)}`}
                      color={gpaColor(heroStats.avgGPA)}
                    />
                  )}
                  {heroStats?.avgUDI != null && (
                    <StatChip
                      label={`Avg UDI: ${heroStats.avgUDI.toFixed(1)}`}
                      color={getUDIColor(heroStats.avgUDI)}
                    />
                  )}
                </div>
              </div>

              {/* Related groups pills */}
              {data.relatedGroups.length > 0 && (
                <div style={{ maxWidth: 280 }}>
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: 8,
                    }}
                  >
                    Related
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {data.relatedGroups.slice(0, 4).map(g => (
                      <Link
                        key={g.identifier}
                        to="/g/$groupId"
                        params={{ groupId: g.identifier }}
                        className="no-underline"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.7)',
                          background: 'rgba(255,255,255,0.08)',
                          padding: '4px 10px',
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        {cleanGroupName(g.shortName ?? g.name)}
                      </Link>
                    ))}
                    {data.relatedGroups.length > 4 && (
                      <span
                        style={{
                          fontSize: 12,
                          color: 'rgba(255,255,255,0.4)',
                          padding: '4px 10px',
                        }}
                      >
                        +{data.relatedGroups.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      {/* ── Main layout ──────────────────────────────────────────────── */}
      <div className="new-container" style={{ paddingTop: 24, paddingBottom: 64 }}>
        {/* Mobile sidebar toggle */}
        <button
          className="md:hidden w-full mb-4"
          onClick={() => setMobileSidebarOpen(v => !v)}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: '1px solid rgba(13,27,42,0.15)',
            background: '#fff',
            textAlign: 'left',
            fontSize: 14,
            fontWeight: 600,
            color: '#0D1B2A',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Browse Departments</span>
          <span style={{ color: '#94A3B8' }}>{mobileSidebarOpen ? '▲' : '▼'}</span>
        </button>

        <div className="flex gap-6 items-start">
          {/* ── Sidebar ─────────────────────────────────────────────── */}
          <aside
            style={{
              width: 240,
              flexShrink: 0,
              display: mobileSidebarOpen ? 'block' : undefined,
            }}
            className="hidden md:block"
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid rgba(13,27,42,0.07)',
                overflow: 'hidden',
                position: 'sticky',
                top: 16,
                maxHeight: 'calc(100vh - 40px)',
                overflowY: 'auto',
              }}
            >
              <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(13,27,42,0.06)' }}>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#94A3B8',
                    margin: 0,
                  }}
                >
                  Departments
                </p>
              </div>
              {sidebarByCategory.map(([cat, items]) => (
                <div key={cat}>
                  <div
                    style={{
                      padding: '8px 16px 4px',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      color: '#CBD5E1',
                    }}
                  >
                    {cat}
                  </div>
                  {items.map(item => {
                    const isActive = String(item.key) === groupId
                    return (
                      <Link
                        key={String(item.key)}
                        to="/g/$groupId"
                        params={{ groupId: String(item.key) }}
                        preload="intent"
                        className="block no-underline"
                        style={{
                          padding: '7px 16px',
                          fontSize: 13,
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'var(--school-primary)' : '#374151',
                          background: isActive ? 'color-mix(in srgb, var(--school-primary) 10%, transparent)' : 'transparent',
                          borderLeft: isActive ? '3px solid var(--school-primary)' : '3px solid transparent',
                          display: 'block',
                          transition: 'all 0.12s',
                        }}
                        onMouseEnter={e => {
                          if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(13,27,42,0.04)'
                        }}
                        onMouseLeave={e => {
                          if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'
                        }}
                      >
                        {cleanGroupName(String(item.title))}
                      </Link>
                    )
                  })}
                </div>
              ))}
            </div>
          </aside>

          {/* Mobile sidebar overlay */}
          {mobileSidebarOpen && (
            <div
              className="md:hidden"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 40,
              }}
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          {/* ── Content ──────────────────────────────────────────────── */}
          <main style={{ flex: 1, minWidth: 0 }}>
            {isPending ? (
              <LoadingSkeleton />
            ) : DID_LOAD ? (
              layout === 'RelatedGroupList' ? (
                <RelatedGroupGrid data={data} />
              ) : (
                <>
                  {/* Top Courses */}
                  {topCourses.length > 0 && (
                    <section style={{ marginBottom: 40 }}>
                      <SectionHeader
                        title="Top Courses in This Department"
                        subtitle="By enrollment and student demand"
                      />
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                          gap: 16,
                          marginBottom: 16,
                        }}
                      >
                        {topCourses.map(course => (
                          <CourseCard
                            key={course._id}
                            courseCode={course._id}
                            courseName={course.description}
                            avgGPA={course.GPA.average > 0 ? course.GPA.average : null}
                            grades={gradeCountsFromEnrollment(course.enrollment)}
                            schoolId="uh"
                            href={`/c/${encodeURIComponent(course._id)}`}
                          />
                        ))}
                      </div>
                      {data.courses.length > 6 && (
                        <button
                          onClick={() => setShowAllCourses(v => !v)}
                          style={{
                            fontSize: 13,
                            color: 'var(--school-primary)',
                            fontWeight: 600,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          {showAllCourses
                            ? 'Show fewer ↑'
                            : `View all ${data.courses.length} courses →`}
                        </button>
                      )}
                    </section>
                  )}

                  {/* Course Table */}
                  <section style={{ marginBottom: 40 }}>
                    <SectionHeader
                      title="All Courses"
                      subtitle="Click any column header to sort"
                      badge={`${courseRows.length}`}
                    />
                    <CourseTable
                      rows={sortedRows}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      maxUDIId={maxUDIId}
                      minUDIId={minUDIId}
                      onSort={handleSort}
                      sortIcon={sortIcon}
                    />
                  </section>

                  {/* Insights Panel */}
                  {insights && (
                    <section>
                      <SectionHeader
                        title="Department Insights"
                        subtitle="The key facts at a glance"
                      />
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                          gap: 16,
                        }}
                      >
                        <InsightCard
                          emoji="🔥"
                          title="Hardest Course"
                          subtitle={insights.hardest.name}
                          course={insights.hardest.id}
                          href={insights.hardest.href}
                          metric={`UDI ${insights.hardest.udi.toFixed(1)}`}
                          metricColor={getUDIColor(insights.hardest.udi)}
                          detail={`W Rate: ${insights.hardest.wRate.toFixed(1)}%`}
                        />
                        {insights.easiest && (
                          <InsightCard
                            emoji="✅"
                            title="Easiest Course"
                            subtitle={insights.easiest.name}
                            course={insights.easiest.id}
                            href={insights.easiest.href}
                            metric={`UDI ${insights.easiest.udi.toFixed(1)}`}
                            metricColor={getUDIColor(insights.easiest.udi)}
                            detail={`Avg GPA: ${insights.easiest.avgGPA.toFixed(2)}`}
                          />
                        )}
                        {insights.mostPopular && (
                          <InsightCard
                            emoji="📊"
                            title="Most Enrolled"
                            subtitle={insights.mostPopular.description}
                            course={insights.mostPopular._id}
                            href={`/c/${encodeURIComponent(insights.mostPopular._id)}`}
                            metric={`${(insights.mostPopular.enrollment.totalEnrolled ?? 0).toLocaleString()} students`}
                            metricColor="var(--school-primary)"
                            detail={`${insights.mostPopular.sectionCount} sections`}
                          />
                        )}
                      </div>
                    </section>
                  )}
                </>
              )
            ) : null}
          </main>
        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function StatChip({ label, color }: { label: string; color?: string }) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: 20,
        background: 'rgba(255,255,255,0.09)',
        color: color ?? 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(255,255,255,0.12)',
        fontFamily: 'var(--font-family-sans)',
      }}
    >
      {label}
    </span>
  )
}

function SectionHeader({
  title,
  subtitle,
  badge,
}: {
  title: string
  subtitle?: string
  badge?: string
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
        <h2
          style={{
            fontSize: 19,
            fontWeight: 700,
            color: '#0D1B2A',
            margin: 0,
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {title}
        </h2>
        {badge && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#94A3B8',
              background: 'rgba(148,163,184,0.12)',
              padding: '2px 7px',
              borderRadius: 6,
            }}
          >
            {badge}
          </span>
        )}
      </div>
      {subtitle && (
        <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>{subtitle}</p>
      )}
    </div>
  )
}

function CourseTable({
  rows,
  sortKey,
  sortDir,
  maxUDIId,
  minUDIId,
  onSort,
  sortIcon,
}: {
  rows: CourseTableRow[]
  sortKey: SortKey
  sortDir: 'asc' | 'desc'
  maxUDIId: string | null
  minUDIId: string | null
  onSort: (key: SortKey) => void
  sortIcon: (key: SortKey) => string
}) {
  const thStyle = (key: SortKey): React.CSSProperties => ({
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: sortKey === key ? 'var(--school-primary)' : '#94A3B8',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    fontFamily: 'var(--font-family-sans)',
  })

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        border: '1px solid rgba(13,27,42,0.07)',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(13,27,42,0.04)',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(13,27,42,0.07)' }}>
              <th style={thStyle('name')} onClick={() => onSort('name')}>
                Course{sortIcon('name')}
              </th>
              <th style={thStyle('gpa')} onClick={() => onSort('gpa')}>
                GPA{sortIcon('gpa')}
              </th>
              <th style={thStyle('udi')} onClick={() => onSort('udi')}>
                UDI{sortIcon('udi')}
              </th>
              <th style={{ ...thStyle('wRate'), display: undefined }} className="hidden md:table-cell" onClick={() => onSort('wRate')}>
                W Rate{sortIcon('wRate')}
              </th>
              <th style={thStyle('sections')} className="hidden md:table-cell" onClick={() => onSort('sections')}>
                Sections{sortIcon('sections')}
              </th>
              <th style={thStyle('instructors')} className="hidden md:table-cell" onClick={() => onSort('instructors')}>
                Instructors{sortIcon('instructors')}
              </th>
              <th style={{ padding: '10px 12px', width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const isHardest = row.id === maxUDIId
              const isEasiest = row.id === minUDIId
              const rowBg = isHardest
                ? 'rgba(239,68,68,0.04)'
                : isEasiest
                  ? 'rgba(16,185,129,0.04)'
                  : idx % 2 === 0
                    ? '#fff'
                    : 'rgba(248,250,252,0.8)'
              return (
                <tr
                  key={row.id}
                  style={{
                    background: rowBg,
                    borderBottom: '1px solid rgba(13,27,42,0.04)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => {
                    ;(e.currentTarget as HTMLTableRowElement).style.background =
                      'rgba(var(--school-primary-rgb),0.04)'
                  }}
                  onMouseLeave={e => {
                    ;(e.currentTarget as HTMLTableRowElement).style.background = rowBg
                  }}
                >
                  {/* Course */}
                  <td style={{ padding: '10px 12px' }}>
                    <Link
                      to="/c/$courseName"
                      params={{ courseName: row.id }}
                      className="no-underline"
                    >
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--school-primary)',
                          display: 'block',
                        }}
                      >
                        {row.id}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: '#64748B',
                          display: 'block',
                          marginTop: 1,
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.name}
                      </span>
                    </Link>
                  </td>

                  {/* GPA */}
                  <td style={{ padding: '10px 12px' }}>
                    {row.avgGPA > 0 ? (
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 13,
                          fontWeight: 600,
                          color: gpaColor(row.avgGPA),
                        }}
                      >
                        {row.avgGPA.toFixed(2)}
                      </span>
                    ) : (
                      <span style={{ color: '#CBD5E1', fontSize: 12 }}>—</span>
                    )}
                  </td>

                  {/* UDI */}
                  <td style={{ padding: '10px 12px' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 700,
                        color: getUDIColor(row.udi),
                        background: `${getUDIColor(row.udi)}18`,
                        padding: '3px 8px',
                        borderRadius: 6,
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      {row.udi.toFixed(1)}
                      <span style={{ fontFamily: 'var(--font-family-sans)', fontSize: 10, fontWeight: 600 }}>
                        {row.udiLabel}
                      </span>
                    </span>
                  </td>

                  {/* W Rate */}
                  <td style={{ padding: '10px 12px' }} className="hidden md:table-cell">
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 12,
                        fontWeight: 600,
                        color: wRateColor(row.wRate),
                      }}
                    >
                      {row.wRate.toFixed(1)}%
                    </span>
                  </td>

                  {/* Sections */}
                  <td style={{ padding: '10px 12px', color: '#64748B', fontSize: 13 }} className="hidden md:table-cell">
                    {row.sections}
                  </td>

                  {/* Instructors */}
                  <td style={{ padding: '10px 12px', color: '#64748B', fontSize: 13 }} className="hidden md:table-cell">
                    {row.instructors}
                  </td>

                  {/* Link arrow */}
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <Link
                      to="/c/$courseName"
                      params={{ courseName: row.id }}
                      className="no-underline"
                      style={{ color: 'var(--school-primary)', fontSize: 16, fontWeight: 700 }}
                    >
                      →
                    </Link>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>
                  No courses found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function InsightCard({
  emoji,
  title,
  subtitle,
  course,
  href,
  metric,
  metricColor,
  detail,
}: {
  emoji: string
  title: string
  subtitle: string
  course: string
  href: string
  metric: string
  metricColor: string
  detail: string
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        padding: '20px',
        border: '1px solid rgba(13,27,42,0.07)',
        boxShadow: '0 2px 8px rgba(13,27,42,0.04)',
      }}
    >
      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{emoji}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: '#94A3B8',
          }}
        >
          {title}
        </span>
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: metricColor,
          fontFamily: 'var(--font-family-mono)',
          marginBottom: 4,
        }}
      >
        {metric}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#0D1B2A',
          fontFamily: 'var(--font-family-mono)',
          marginBottom: 2,
        }}
      >
        {course}
      </div>
      <div
        style={{
          fontSize: 12,
          color: '#64748B',
          marginBottom: 8,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {subtitle}
      </div>
      <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 12 }}>{detail}</div>
      <Link
        to="/c/$courseName"
        params={{ courseName: course }}
        className="no-underline"
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--school-primary)',
        }}
      >
        View course →
      </Link>
    </div>
  )
}

function RelatedGroupGrid({ data }: { data: PopulatedGroupResult }) {
  return (
    <div>
      <SectionHeader
        title={cleanGroupName(data.name)}
        subtitle={data.description}
        badge={`${data.relatedGroups.length} groups`}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
        }}
      >
        {data.relatedGroups
          .toSorted((a, b) =>
            cleanGroupName(a.name)
              .replace(/[^\w]/g, '')
              .localeCompare(cleanGroupName(b.name).replace(/[^\w]/g, '')),
          )
          .map(group => (
            <Link
              key={group.identifier}
              to="/g/$groupId"
              params={{ groupId: group.identifier }}
              preload="intent"
              className="block no-underline"
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: '18px 20px',
                  border: '1px solid rgba(13,27,42,0.07)',
                  borderLeft: '4px solid var(--school-primary)',
                  boxShadow: '0 2px 8px rgba(13,27,42,0.04)',
                  transition: 'all 0.16s',
                  height: '100%',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget
                  el.style.transform = 'translateY(-2px)'
                  el.style.boxShadow = '0 8px 24px rgba(13,27,42,0.10)'
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget
                  el.style.transform = 'translateY(0)'
                  el.style.boxShadow = '0 2px 8px rgba(13,27,42,0.04)'
                }}
              >
                <h3
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#0D1B2A',
                    margin: 0,
                    marginBottom: 6,
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {cleanGroupName(group.name)}
                </h3>
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: 12, color: '#64748B' }}>
                    {group.courseCount} course{group.courseCount !== 1 ? 's' : ''}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--school-primary)', fontWeight: 600 }}>
                    Explore →
                  </span>
                </div>
              </div>
            </Link>
          ))}
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div>
      {/* Top courses skeleton */}
      <div style={{ marginBottom: 40 }}>
        <Skeleton height={22} width={240} className="mb-2" />
        <Skeleton height={14} width={180} className="mb-5" />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <CourseCardSkeleton key={i} />
          ))}
        </div>
      </div>
      {/* Table skeleton */}
      <Skeleton height={22} width={140} className="mb-2" />
      <Skeleton height={14} width={200} className="mb-4" />
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid rgba(13,27,42,0.07)',
          overflow: 'hidden',
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4"
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid rgba(13,27,42,0.05)',
            }}
          >
            <Skeleton height={14} width={70} />
            <Skeleton height={14} width={120} />
            <Skeleton height={14} width={40} />
            <Skeleton height={14} width={40} />
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Not found ─────────────────────────────────────────────────────────────

function NotFoundComponent() {
  const { groupId } = Route.useParams()
  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="new-container" style={{ paddingTop: 48, paddingBottom: 64 }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            padding: '40px',
            border: '1px solid rgba(239,68,68,0.15)',
            borderLeft: '4px solid #EF4444',
            maxWidth: 480,
          }}
        >
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#EF4444',
              marginBottom: 8,
            }}
          >
            Error 404
          </p>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: '#0D1B2A',
              marginBottom: 12,
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            Group not found
          </h1>
          <p style={{ color: '#64748B', marginBottom: 20 }}>
            Group{' '}
            <code
              style={{
                background: 'rgba(13,27,42,0.06)',
                padding: '2px 6px',
                borderRadius: 4,
                fontFamily: 'var(--font-family-mono)',
                fontSize: 13,
              }}
            >
              {groupId}
            </code>{' '}
            could not be found.
          </p>
          <Link
            to="/courses"
            className="no-underline"
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--school-primary)',
            }}
          >
            ← Browse all departments
          </Link>
        </div>
      </div>
    </div>
  )
}
